import { useCallback, useEffect, useState } from 'react';
import {
  Container, Grid, Card, CardActionArea, CardContent,
  Typography, TextField, Chip, Box, Divider,
  Table, TableHead, TableRow, TableCell, TableBody,
  Collapse, IconButton, ToggleButton, ToggleButtonGroup,
  CircularProgress, Alert
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import HotelIcon from '@mui/icons-material/Hotel';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PaymentsIcon from '@mui/icons-material/Payments';
import SidebarLayout from '../components/sidebarLayout';
import { useTranslation } from 'react-i18next';
import {
  getAllClienti,
  getOrdiniByCliente,
  getAllCompanies,
  getCompanyClients,
  getOrdiniByHeliteRoom,
} from '../APIs/ClientsAPI';
import { getAllPrenotazioni } from '../APIs/ReservationsAPI';
import { getHeliteCheckedInGuests } from '../APIs/HeliteAPI';
import { localizedErrorMessage } from '../utils/italianErrorMessage';

const STATUS_COLORS = {
  PAID:   'success',
  CLOSED: 'warning',
  OPEN:   'info',
};

// Handles: [], { data: [] }, { clients: [] }, { orders: [] }, { items: [] }
function normalizeArray(res) {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== 'object') return [];
  const key = ['data', 'clients', 'orders', 'items', 'results', 'companies', 'guests'].find(k => Array.isArray(res[k]));
  return key ? res[key] : [];
}

function getOrderTotal(order) {
  const productsTotal = (order.orderProducts || []).reduce(
    (sum, p) => sum + (p.unitPrice ?? p.product?.price ?? 0) * p.quantity, 0
  );
  const packagesTotal = (order.orderPackages || []).reduce(
    (sum, p) => sum + (p.unitPrice ?? p.package?.price ?? 0) * p.quantity, 0
  );
  const fixedMenusTotal = (order.orderFixedPriceMenus || []).reduce(
    (sum, p) => sum + Number(p.finalPrice ?? 0) * Number(p.quantity ?? 1), 0
  );

  return productsTotal + packagesTotal + fixedMenusTotal;
}

function buildHeliteRoomTargets(reservations = [], guests = []) {
  const targets = new Map();
  const ensureTarget = (roomNumber) => {
    const key = String(roomNumber || '').trim();
    if (!key) return null;
    if (!targets.has(key)) {
      targets.set(key, {
        roomNumber: key,
        holderName: '',
        billNumber: '',
        guestCount: 0,
        reservationsCount: 0,
        isCheckedIn: false,
        latestDate: null,
      });
    }
    return targets.get(key);
  };

  reservations.forEach((reservation) => {
    const current = ensureTarget(reservation?.heliteRoomNumber);
    if (!current) return;

    const startDate = reservation?.startDate ? new Date(reservation.startDate) : null;
    const shouldUseReservation =
      !current.latestDate ||
      (startDate && !Number.isNaN(startDate.getTime()) && startDate > current.latestDate);

    targets.set(current.roomNumber, {
      ...current,
      holderName: shouldUseReservation ? (reservation?.heliteHolderName || current.holderName) : current.holderName,
      billNumber: shouldUseReservation ? (reservation?.heliteBillNumber || current.billNumber) : current.billNumber,
      reservationsCount: current.reservationsCount + 1,
      latestDate: shouldUseReservation ? startDate : current.latestDate,
    });
  });

  guests.forEach((guest) => {
    const current = ensureTarget(guest?.roomnr || guest?.heliteRoomNumber || guest?.roomNumber);
    if (!current) return;

    targets.set(current.roomNumber, {
      ...current,
      holderName: guest?.holderName || current.holderName,
      billNumber: guest?.number || current.billNumber,
      billId: guest?.id || current.billId,
      idrispren: guest?.idrispren || current.idrispren,
      arrival: guest?.arrival || current.arrival,
      departure: guest?.departure || current.departure,
      guestCount: current.guestCount + 1,
      isCheckedIn: true,
    });
  });

  return [...targets.values()].sort((a, b) =>
    String(a.roomNumber).localeCompare(String(b.roomNumber), undefined, { numeric: true, sensitivity: 'base' })
  );
}

function OrderRow({ order, t }) {
  const [open, setOpen] = useState(false);
  const total = getOrderTotal(order);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size="small" onClick={() => setOpen(o => !o)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>#{order.id}</TableCell>
        <TableCell>
          {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '—'}
        </TableCell>
        <TableCell>
          <Chip
            label={t(`reports.orderStatus.${order.status}`, { defaultValue: order.status })}
            color={STATUS_COLORS[order.status] ?? 'default'}
            size="small"
          />
        </TableCell>
        <TableCell align="right"><strong>€{total.toFixed(2)}</strong></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0 }}>
          <Collapse in={open} unmountOnExit>
            <Box sx={{ py: 1, px: 2 }}>
              {(order.orderProducts || []).map((p, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                  <Typography variant="body2">
                    (x{p.quantity}) {p.product?.name ?? t('orderHistoryPage.productFallback', { id: p.productId })}
                  </Typography>
                  <Typography variant="body2">
                    €{((p.unitPrice ?? p.product?.price ?? 0) * p.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              {(order.orderPackages || []).map((p, i) => (
                <Box key={`pkg-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                  <Typography variant="body2">
                    (x{p.quantity}) {p.package?.name ?? t('orderHistoryPage.packageFallback', { id: p.packageId })}
                  </Typography>
                  <Typography variant="body2">
                    €{((p.unitPrice ?? p.package?.price ?? 0) * p.quantity).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              {(order.orderFixedPriceMenus || []).map((p, i) => (
                <Box key={`menu-${i}`} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.3 }}>
                  <Typography variant="body2">
                    (x{p.quantity ?? 1}) {p.menuNameSnapshot ?? t('orderHistoryPage.fixedMenuFallback')}
                  </Typography>
                  <Typography variant="body2">
                    â‚¬{(Number(p.finalPrice ?? 0) * Number(p.quantity ?? 1)).toFixed(2)}
                  </Typography>
                </Box>
              ))}
              {(order.orderProducts || []).length === 0 && (order.orderPackages || []).length === 0 && (order.orderFixedPriceMenus || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">{t('orderHistoryPage.noProducts')}</Typography>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

function HeliteRoomSummary({ selected, orders, totalSpent, t }) {
  if (selected?.type !== 'heliteRoom') return null;

  const lastOrderDate = orders
    .map((order) => order?.createdAt ? new Date(order.createdAt) : null)
    .filter((date) => date && !Number.isNaN(date.getTime()))
    .sort((a, b) => b - a)[0];

  const metrics = [
    {
      icon: <PaymentsIcon fontSize="small" />,
      label: t('orderHistoryPage.spentShort'),
      value: `EUR ${totalSpent.toFixed(2)}`,
    },
    {
      icon: <ReceiptLongIcon fontSize="small" />,
      label: t('orderHistoryPage.orders'),
      value: String(orders.length),
    },
    {
      icon: <CalendarMonthIcon fontSize="small" />,
      label: t('orderHistoryPage.lastOrder'),
      value: lastOrderDate ? lastOrderDate.toLocaleDateString() : '-',
    },
  ];

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(135deg, rgba(25,118,210,0.20), rgba(76,175,80,0.10) 55%, rgba(255,152,0,0.12))',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box
            sx={{
              width: 54,
              height: 54,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'success.dark',
              color: 'success.contrastText',
              boxShadow: '0 10px 24px rgba(76,175,80,0.25)',
              flexShrink: 0,
            }}
          >
            <HotelIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="overline" color="text.secondary">
              HElite
            </Typography>
            <Typography variant="h5" sx={{ lineHeight: 1.1 }}>
              {t('orderHistoryPage.roomLabel', { roomNumber: selected.data.roomNumber })}
            </Typography>
            {selected.data.holderName && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {selected.data.holderName}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {selected.data.isCheckedIn && (
            <Chip label={t('orderHistoryPage.inHouse')} color="success" size="small" />
          )}
          {selected.data.billNumber && (
            <Chip
              label={t('orderHistoryPage.heliteBill', { billNumber: selected.data.billNumber })}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      </Box>

      <Grid container spacing={1.5}>
        {metrics.map((metric) => (
          <Grid item xs={12} sm={4} key={metric.label}>
            <Box
              sx={{
                p: 1.25,
                borderRadius: 1,
                bgcolor: 'rgba(0,0,0,0.18)',
                border: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Box sx={{ color: 'primary.light', display: 'flex' }}>{metric.icon}</Box>
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {metric.label}
                </Typography>
                <Typography variant="subtitle2">
                  {metric.value}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export default function StoricoOrdiniCliente() {
  const { t } = useTranslation();

  const token  = localStorage.getItem('accessToken');
  const tenant = localStorage.getItem('tenantId');

  const [section,      setSection]      = useState('clienti');
  const [clienti,      setClienti]      = useState([]);
  const [companies,    setCompanies]    = useState([]);
  const [heliteRooms,  setHeliteRooms]  = useState([]);
  const [query,        setQuery]        = useState('');
  const [selected,     setSelected]     = useState(null);
  const [orders,       setOrders]       = useState([]);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);

  const fetchClienti = useCallback(async () => {
    try {
      const raw = await getAllClienti(token, tenant);
      const list = normalizeArray(raw);
      setClienti(list);
    } catch (e) {
      setError(localizedErrorMessage(e));
    }
  }, [token, tenant]);

  const fetchCompanies = useCallback(async () => {
    try {
      const raw = await getAllCompanies(token, tenant);
      const list = normalizeArray(raw);
      setCompanies(list);
    } catch (e) {
      console.error('[StoricoOrdini] fetchCompanies error:', e);
    }
  }, [token, tenant]);

  const fetchHeliteRooms = useCallback(async () => {
    const [reservationsResult, guestsResult] = await Promise.allSettled([
      getAllPrenotazioni(token, tenant, { noCache: true }),
      getHeliteCheckedInGuests(token, tenant),
    ]);

    if (reservationsResult.status === 'rejected') {
      console.error('[StoricoOrdini] fetchHeliteReservations error:', reservationsResult.reason);
    }
    if (guestsResult.status === 'rejected') {
      console.error('[StoricoOrdini] fetchHeliteGuests error:', guestsResult.reason);
    }

    const reservations = reservationsResult.status === 'fulfilled'
      ? normalizeArray(reservationsResult.value)
      : [];
    const guests = guestsResult.status === 'fulfilled'
      ? normalizeArray(guestsResult.value)
      : [];

    setHeliteRooms(buildHeliteRoomTargets(reservations, guests));
  }, [token, tenant]);

  useEffect(() => {
    fetchClienti();
    fetchCompanies();
    fetchHeliteRooms();
  }, [fetchClienti, fetchCompanies, fetchHeliteRooms]);

  const handleSelectClient = async (client) => {
    setSelected({ type: 'client', data: client });
    setOrders([]);
    setStatusFilter('ALL');
    setError(null);
    setLoading(true);
    try {
      const raw = await getOrdiniByCliente(client.id, token, tenant);
      const list = normalizeArray(raw);
      setOrders(list);
    } catch (e) {
      console.error('[StoricoOrdini] getOrdiniByCliente error:', e);
      setError(localizedErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCompany = async (company) => {
    setSelected({ type: 'company', data: company });
    setOrders([]);
    setStatusFilter('ALL');
    setError(null);
    setLoading(true);
    try {
      const rawClients = await getCompanyClients(company.id, token, tenant);
      const clientList = normalizeArray(rawClients);
      const allOrders = await Promise.all(
        clientList.map(c =>
          getOrdiniByCliente(c.id, token, tenant)
            .then(r => normalizeArray(r))
            .catch(() => [])
        )
      );
      setOrders(allOrders.flat());
    } catch (e) {
      console.error('[StoricoOrdini] handleSelectCompany error:', e);
      setError(localizedErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectHeliteRoom = async (room) => {
    const roomNumber = String(room?.roomNumber || room || '').trim();
    if (!roomNumber) return;

    setSelected({ type: 'heliteRoom', data: { ...(typeof room === 'object' ? room : {}), roomNumber } });
    setOrders([]);
    setStatusFilter('ALL');
    setError(null);
    setLoading(true);
    try {
      const raw = await getOrdiniByHeliteRoom(roomNumber, token, tenant);
      const list = normalizeArray(raw);
      setOrders(list);
    } catch (e) {
      console.error('[StoricoOrdini] getOrdiniByHeliteRoom error:', e);
      setError(localizedErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const filteredOrders = statusFilter === 'ALL'
    ? orders
    : orders.filter(o => o.status === statusFilter);

  const totalSpent = orders
    .filter(o => ['PAID', 'CLOSED'].includes(o.status))
    .reduce((sum, o) => sum + getOrderTotal(o), 0);

  const q = query.trim().toLowerCase();

  const listaClienti = q
    ? clienti.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.surname?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q)
      )
    : clienti;

  const listaAziende = q
    ? companies.filter(c =>
        c.companyName?.toLowerCase().includes(q) ||
        c.vatNumber?.toLowerCase().includes(q)
      )
    : companies;

  const listaHeliteRooms = q
    ? heliteRooms.filter(room =>
        room.roomNumber?.toLowerCase().includes(q) ||
        room.holderName?.toLowerCase().includes(q) ||
        room.billNumber?.toLowerCase().includes(q) ||
        room.billId?.toLowerCase().includes(q) ||
        room.idrispren?.toLowerCase().includes(q)
      )
    : heliteRooms;

  const showManualHeliteRoomSearch = section === 'heliteRooms'
    && query.trim()
    && !listaHeliteRooms.some(room => room.roomNumber === query.trim());

  return (
    <SidebarLayout>
      <Container sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          {t('orderHistoryPage.title')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <ToggleButtonGroup
          value={section}
          exclusive
          onChange={(_, val) => {
            if (!val) return;
            setSection(val);
            setSelected(null);
            setOrders([]);
            setQuery('');
            setError(null);
          }}
          sx={{ mb: 3 }}
        >
          <ToggleButton value="clienti">
            {t('orderHistoryPage.clients')}
            {clienti.length > 0 && (
              <Chip label={clienti.length} size="small" sx={{ ml: 1 }} />
            )}
          </ToggleButton>
          <ToggleButton value="aziende">
            {t('orderHistoryPage.companies')}
            {companies.length > 0 && (
              <Chip label={companies.length} size="small" sx={{ ml: 1 }} />
            )}
          </ToggleButton>
          <ToggleButton value="heliteRooms">
            {t('orderHistoryPage.heliteRooms')}
            {heliteRooms.length > 0 && (
              <Chip label={heliteRooms.length} size="small" sx={{ ml: 1 }} />
            )}
          </ToggleButton>
        </ToggleButtonGroup>

        <Grid container spacing={3}>

          {/* LEFT: client/company list */}
          <Grid item xs={12} md={4}>
            {section === 'heliteRooms' && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, rgba(25,118,210,0.18), rgba(46,125,50,0.12))',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('orderHistoryPage.heliteRoomsPanel')}
                    </Typography>
                    <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                      {t('orderHistoryPage.heliteGuestsCount', { count: heliteRooms.filter(room => room.isCheckedIn).length })}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={t('orderHistoryPage.inHouse')}
                  />
                </Box>
              </Box>
            )}

            <TextField
              fullWidth
              size="small"
              label={t('orderHistoryPage.search')}
              value={query}
              onChange={e => setQuery(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Box sx={{ maxHeight: '65vh', overflowY: 'auto', pr: 0.5 }}>

              {section === 'clienti' && listaClienti.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  {t('orderHistoryPage.noClients')}
                </Typography>
              )}

              {section === 'clienti' && listaClienti.map(c => (
                <Card
                  key={c.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    border: selected?.data?.id === c.id ? '2px solid' : undefined,
                    borderColor: selected?.data?.id === c.id ? 'primary.main' : undefined,
                  }}
                >
                  <CardActionArea onClick={() => handleSelectClient(c)}>
                    <CardContent sx={{ py: 1.2, px: 2 }}>
                      <Typography variant="subtitle2">
                        {c.name} {c.surname}
                      </Typography> 
                      {c.email && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.email}
                        </Typography>
                      )}
                      {c.phone && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {c.phone}
                        </Typography>
                      )}
                      {c.company?.companyName && (
                        <Chip
                          label={c.company.companyName}
                          size="small"
                          variant="outlined"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}

              {section === 'aziende' && listaAziende.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  {t('orderHistoryPage.noCompanies')}
                </Typography>
              )}

              {section === 'aziende' && listaAziende.map(company => (
                <Card
                  key={company.id}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    border: selected?.data?.id === company.id ? '2px solid' : undefined,
                    borderColor: selected?.data?.id === company.id ? 'primary.main' : undefined,
                  }}
                >
                  <CardActionArea onClick={() => handleSelectCompany(company)}>
                    <CardContent sx={{ py: 1.2, px: 2 }}>
                      <Typography variant="subtitle2">🏢 {company.companyName}</Typography>
                      {company.vatNumber && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          P.IVA: {company.vatNumber}
                        </Typography>
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}

              {section === 'heliteRooms' && listaHeliteRooms.length === 0 && !showManualHeliteRoomSearch && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  {t('orderHistoryPage.noHeliteRooms')}
                </Typography>
              )}

              {section === 'heliteRooms' && showManualHeliteRoomSearch && (
                <Card
                  variant="outlined"
                  sx={{
                    mb: 1,
                    border: selected?.type === 'heliteRoom' && selected?.data?.roomNumber === query.trim() ? '2px solid' : undefined,
                    borderColor: selected?.type === 'heliteRoom' && selected?.data?.roomNumber === query.trim() ? 'primary.main' : undefined,
                  }}
                >
                  <CardActionArea onClick={() => handleSelectHeliteRoom(query.trim())}>
                    <CardContent sx={{ py: 1.2, px: 2 }}>
                      <Typography variant="subtitle2">
                        {t('orderHistoryPage.searchHeliteRoom', { roomNumber: query.trim() })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {t('orderHistoryPage.searchHeliteRoomHint')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              )}

              {section === 'heliteRooms' && listaHeliteRooms.map(room => (
                <Card
                  key={room.roomNumber}
                  variant="outlined"
                  sx={{
                    mb: 1,
                    overflow: 'hidden',
                    border: selected?.type === 'heliteRoom' && selected?.data?.roomNumber === room.roomNumber ? '1px solid' : undefined,
                    borderColor: selected?.type === 'heliteRoom' && selected?.data?.roomNumber === room.roomNumber ? 'warning.main' : 'divider',
                    background: selected?.type === 'heliteRoom' && selected?.data?.roomNumber === room.roomNumber
                      ? 'linear-gradient(135deg, rgba(255,152,0,0.18), rgba(25,118,210,0.08))'
                      : 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))',
                  }}
                >
                  <CardActionArea onClick={() => handleSelectHeliteRoom(room)}>
                    <CardContent sx={{ py: 1.3, px: 1.6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.2 }}>
                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: room.isCheckedIn ? 'success.dark' : 'action.selected',
                            color: room.isCheckedIn ? 'success.contrastText' : 'text.secondary',
                            flexShrink: 0,
                          }}
                        >
                          <HotelIcon fontSize="small" />
                        </Box>

                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                            <Typography variant="subtitle2" noWrap>
                              {t('orderHistoryPage.roomLabel', { roomNumber: room.roomNumber })}
                            </Typography>
                            {room.isCheckedIn && (
                              <Chip size="small" color="success" label={t('orderHistoryPage.inHouse')} sx={{ height: 20 }} />
                            )}
                          </Box>

                          {room.holderName && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                              <PersonIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {room.holderName}
                              </Typography>
                            </Box>
                          )}

                          {(room.billNumber || room.idrispren) && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, minWidth: 0 }}>
                              <ReceiptLongIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {room.billNumber
                                  ? t('orderHistoryPage.heliteBill', { billNumber: room.billNumber })
                                  : room.idrispren}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Grid>

          {/* RIGHT: orders */}
          <Grid item xs={12} md={8}>
            {!selected && (
              <Box sx={{ mt: 6, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('orderHistoryPage.selectTarget')}
                </Typography>
              </Box>
            )}

            {selected && (
              <>
                <HeliteRoomSummary selected={selected} orders={orders} totalSpent={totalSpent} t={t} />

                <Box sx={{ display: selected.type === 'heliteRoom' ? 'none' : 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <Typography variant="h6">
                    {selected.type === 'client'
                      ? `${selected.data.name} ${selected.data.surname}`
                      : selected.type === 'heliteRoom'
                        ? t('orderHistoryPage.roomLabel', { roomNumber: selected.data.roomNumber })
                        : `🏢 ${selected.data.companyName}`
                    }
                  </Typography>
                  <Chip
                    label={t('orderHistoryPage.spent', { amount: `€${totalSpent.toFixed(2)}` })}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Chip
                    label={t('orderHistoryPage.ordersCount', { count: orders.length })}
                    size="small"
                    variant="outlined"
                  />
                </Box>

                {selected.type === 'heliteRoomLegacy' && (selected.data.holderName || selected.data.isCheckedIn) && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                    {selected.data.holderName && (
                      <Typography variant="body2" color="text.secondary">
                        {selected.data.holderName}
                      </Typography>
                    )}
                    {selected.data.isCheckedIn && (
                      <Chip
                        label={t('orderHistoryPage.inHouse')}
                        color="success"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                )}

                <ToggleButtonGroup
                  value={statusFilter}
                  exclusive
                  onChange={(_, val) => { if (val) setStatusFilter(val); }}
                  size="small"
                  sx={{ mb: 2 }}
                >
                  {['ALL', 'OPEN', 'CLOSED', 'PAID'].map(s => (
                    <ToggleButton key={s} value={s}>{s}</ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Divider sx={{ mb: 2 }} />

                {loading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                  </Box>
                )}

                {!loading && filteredOrders.length === 0 && (
                  <Typography color="text.secondary">
                    {t('orderHistoryPage.noOrders')}
                  </Typography>
                )}

                {!loading && filteredOrders.length > 0 && (
                  <Table
                    size="small"
                    sx={selected.type === 'heliteRoom' ? {
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                      '& .MuiTableCell-root': {
                        borderColor: 'rgba(255,255,255,0.10)',
                      },
                      '& .MuiTableHead-root .MuiTableCell-root': {
                        bgcolor: 'rgba(255,255,255,0.06)',
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: 12,
                        letterSpacing: 0.4,
                      },
                      '& .MuiTableBody-root > .MuiTableRow-root:hover': {
                        bgcolor: 'rgba(25,118,210,0.08)',
                      },
                    } : undefined}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell />
                        <TableCell>{t('orderHistoryPage.order')}</TableCell>
                        <TableCell>{t('orderHistoryPage.date')}</TableCell>
                        <TableCell>{t('orderHistoryPage.status')}</TableCell>
                        <TableCell align="right">{t('orderHistoryPage.total')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredOrders.map(order => (
                        <OrderRow key={order.id} order={order} t={t} />
                      ))}
                    </TableBody>
                  </Table>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>
    </SidebarLayout>
  );
}
